import { AutoComplete } from "primereact/autocomplete";
import React, { useEffect, useState } from "react";

export interface NominatimResponse {
    display_name: string;
    lat: string;
    lng: string;
}

interface LocationAutocompleteProps {
    label?: string;
    placeholder?: string;
    countryCode?: string;
    onLocationSelect: (location?: NominatimResponse) => void;
    col?: string;
}

const LocationAutocomplete: React.FC<LocationAutocompleteProps> = ({
    label = "Location",
    placeholder = "Enter location or hotel name",
    countryCode,
    onLocationSelect,
    col
}) => {
    const [selected, setSelected] = useState<NominatimResponse>();
    const [suggestions, setSuggestions] = useState<NominatimResponse[]>();
    const [autoService, setAutoService] =
        useState<google.maps.places.AutocompleteService | null>(null);
    const [placesService, setPlacesService] =
        useState<google.maps.places.PlacesService | null>(null);

    useEffect(() => {
        // Load Google Maps script if not already loaded
        if (!(window as any).google) {
            const script = document.createElement("script");
            script.src = `https://maps.googleapis.com/maps/api/js?key=AIzaSyDHoMZqJpvKoLGmSon3tzM7NwARRerh_zQ&libraries=places`;
            script.async = true;
            script.onload = initServices;
            document.body.appendChild(script);
        } else {
            initServices();
        }
    }, []);

    const initServices = () => {
        const autocomplete = new google.maps.places.AutocompleteService();
        const map = document.createElement("div");
        const service = new google.maps.places.PlacesService(map);
        setAutoService(autocomplete);
        setPlacesService(service);
    };

    const fetchSuggestions = (query: string) => {
        if (!autoService || !query) return;

        const request: google.maps.places.AutocompletionRequest = {
            input: query,
            ...(countryCode
                ? { componentRestrictions: { country: countryCode.toLowerCase() } }
                : {}),
        };

        autoService.getPlacePredictions(request, (predictions, status) => {
            if (
                status !== google.maps.places.PlacesServiceStatus.OK ||
                !predictions
            ) {
                setSuggestions([]);
                return;
            }

            const formatted = predictions.map((p) => ({
                display_name: p.description,
                lat: "",
                lon: "",
                place_id: p.place_id!,
            })) as any[];

            setSuggestions(formatted);
        });
    };

    const handleSelect = (selectedItem: any) => {
        if (!placesService || !selectedItem?.place_id) {
            setSelected(selectedItem);
            return;
        }

        placesService.getDetails(
            { placeId: selectedItem.place_id, fields: ["geometry", "name", "formatted_address"] },
            (place, status) => {
                if (
                    status === google.maps.places.PlacesServiceStatus.OK &&
                    place?.geometry?.location
                ) {
                    const loc: NominatimResponse = {
                        display_name: place.formatted_address || selectedItem.display_name,
                        lat: place.geometry.location.lat().toString(),
                        lng: place.geometry.location.lng().toString(),
                    };
                    setSelected(loc);
                    onLocationSelect(loc);
                } else {
                    setSelected(undefined);
                    onLocationSelect(undefined);
                }
            }
        );
    };
    const className = col ? `field col-12 md:col-${col}` : "field col-12 md:col-6";
    return (
        <div className={className}>
            {label && <label>{label}</label>}
            <AutoComplete
                field="display_name"
                value={selected}
                suggestions={suggestions}
                completeMethod={(e) => fetchSuggestions(e.query)}
                onChange={(e) => handleSelect(e.value)}
                placeholder={placeholder} />
        </div>
    );
};

export default LocationAutocomplete;
